import React from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "February 28, 2026";

export default function Terms() {
  return (
    <div data-testid="terms-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
      <div className="label mb-4 text-[hsl(var(--accent))]">Terms of Service</div>
      <h1 className="serif text-5xl text-foreground leading-tight">
        The simple agreement between us.
      </h1>
      <p className="mt-4 mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        Last updated · {LAST_UPDATED}
      </p>

      <Section title="1. Acceptance">
        <p>
          By using Correlate (the &ldquo;Service&rdquo;), you agree to these Terms. If
          you do not agree, please don&apos;t use the Service. These Terms form a binding
          agreement between you and the publisher of Correlate.
        </p>
      </Section>

      <Section title="2. Who can use Correlate">
        <p>
          You must be 13 or older to create an account. By creating an account, you
          confirm that the information you provide is accurate and that you will keep
          it that way.
        </p>
      </Section>

      <Section title="3. Your account">
        <p>
          You are responsible for what happens under your account. Keep your password
          private. Tell us right away if you suspect unauthorized access by writing to
          <a href="mailto:privacy@correlate.online" className="link-underline text-[hsl(var(--primary))]"> privacy@correlate.online</a>.
        </p>
      </Section>

      <Section title="4. Acceptable use">
        <p>You agree NOT to use Correlate to:</p>
        <ul className="mt-3 space-y-2 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li>Harass, threaten, dox, or send unwanted sexual or violent content to other readers.</li>
          <li>Post hate speech, incitement to violence, or sexually exploitative content involving minors.</li>
          <li>Impersonate another person or create accounts for someone else.</li>
          <li>Scrape, spam, or attempt to disrupt the Service.</li>
          <li>Use the Oracle for unlawful purposes, medical or financial advice you would act on without a professional, or to generate content that violates the platform&apos;s LLM provider policies.</li>
        </ul>
        <p className="mt-3">
          We may remove content, suspend, or terminate accounts that violate this section,
          with or without notice when the situation is urgent.
        </p>
      </Section>

      <Section title="5. Your content">
        <p>
          You retain ownership of what you write — Oracle conversations, weekly posts,
          comments, pins, reactions, direct messages, and your bio. By posting public
          content (e.g. weekly correlations), you grant us a non-exclusive license to
          host, display, and distribute that content within Correlate for the purpose
          of running the Service. We do not claim ownership and we do not sell your
          content to third parties.
        </p>
      </Section>

      <Section title="6. The Oracle">
        <p>
          The Oracle is an AI assistant that draws on multiple traditions to help you
          notice correlations. It can be wrong, biased, or speculative. It is not a
          medical professional, lawyer, financial advisor, or mental-health
          practitioner. Do not act on its output in matters where a qualified human is
          warranted. Use your own discernment.
        </p>
      </Section>

      <Section title="7. Service availability">
        <p>
          We strive to keep Correlate online and quick, but the Service is provided
          &ldquo;as is.&rdquo; We may add, change, or remove features. We may pause or
          end the Service. We are not liable for losses arising from downtime or
          interrupted features.
        </p>
      </Section>

      <Section title="8. Termination">
        <p>
          You may delete your account any time by writing to&nbsp;
          <a href="mailto:privacy@correlate.online" className="link-underline text-[hsl(var(--primary))]">
            privacy@correlate.online
          </a>.
          We may suspend or terminate accounts that violate these Terms.
        </p>
      </Section>

      <Section title="9. Disclaimers and limits">
        <p>
          To the maximum extent permitted by law, Correlate and its operators are not
          liable for indirect, incidental, or consequential damages arising from your
          use of the Service. Our total liability for any claim shall not exceed the
          amount you paid us in the preceding 12 months (which for most users is
          $0 — Correlate is free).
        </p>
      </Section>

      <Section title="10. Governing law">
        <p>
          These Terms are governed by the laws of the United States and the state
          where the publisher resides, without regard to conflict-of-law rules.
        </p>
      </Section>

      <Section title="11. Changes">
        <p>
          We may update these Terms when our practices change. The &ldquo;Last
          updated&rdquo; date reflects the most recent version. Material changes
          will be communicated by email or an in-app notice.
        </p>
      </Section>

      <p className="mt-16 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <Link to="/privacy" className="link-underline">See also: Privacy Policy →</Link>
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="serif text-2xl text-foreground mb-3">{title}</h2>
      <div className="serif text-foreground/85 leading-relaxed text-[16px]">
        {children}
      </div>
    </section>
  );
}
